// Shared by Cloud Functions and the frontend (imported there as @shared).
// The offseason calendar and the college transfer portal's rules.
//
// One offseason step a day. Each stage names the step just played:
//   review        the season is archived; the cut window opens
//   cuts          last day to cut players
//   portal-open   seniors graduate, players enter the portal (with the cut
//                 players), pitching opens: portal day 1
//   portal-2..7   each day's pitches count, players start committing
//   portal-close  the last pitches count; everyone left picks a school
//   development   players develop, freshmen fill the open spots
//   new-season    next year's schedule
// Pro leagues have no portal yet: review, development, new-season.

export const PORTAL_DAYS = 7;
export const CUTS_PER_TEAM = 3;
export const PITCHES_PER_DAY = 3;

export const PITCH_TYPES = [
  {key: "playingTime", label: "Playing time",
    text: "You'll play a big role here."},
  {key: "winning", label: "Winning", text: "We win here."},
  {key: "development", label: "Development",
    text: "We'll make you a better player."},
  {key: "prestige", label: "Program prestige",
    text: "This is a program people notice."},
];

const PORTAL_STAGES = [
  "cuts", "portal-open",
  ...Array.from({length: PORTAL_DAYS - 1}, (_, i) => `portal-${i + 2}`),
  "portal-close",
];

// Pro: after development, two days to re-sign your own players (the days
// after "development" and "resign"), draft day (shared/draft.js), then
// free agency (shared/contracts.js).
export const FA_DAYS = 7;
const FA_STAGES = [
  "resign", "draft", "fa-open",
  ...Array.from({length: FA_DAYS - 1}, (_, i) => `fa-${i + 2}`),
  "fa-close",
];

/**
 * The offseason's stages in order.
 * @param {string} type "college" or "pro"
 * @return {string[]}
 */
export function offseasonStages(type) {
  return type === "pro" ?
    ["review", "development", ...FA_STAGES, "new-season"] :
    ["review", ...PORTAL_STAGES, "development", "new-season"];
}

/**
 * Whether pro teams may re-sign their expiring players now: the two days
 * after development.
 * @param {object} league
 * @return {boolean}
 */
export function resignWindowOpen(league) {
  const s = league?.season;
  return league?.type === "pro" && s?.phase === "offseason" &&
    ["development", "resign"].includes(s.offseason?.stage);
}

/**
 * The free agency day (1-7) while teams may make offers, else 0.
 * @param {object} league
 * @return {number}
 */
export function faDay(league) {
  const s = league?.season;
  if (league?.type !== "pro" || s?.phase !== "offseason") return 0;
  const stage = s.offseason?.stage;
  if (stage === "fa-open") return 1;
  const m = /^fa-(\d+)$/.exec(stage ?? "");
  return m ? Number(m[1]) : 0;
}

/**
 * Whether coaches may cut players now: the two days after the season is
 * archived, before the portal opens.
 * @param {object} league
 * @return {boolean}
 */
export function cutWindowOpen(league) {
  const s = league?.season;
  return league?.type !== "pro" && s?.phase === "offseason" &&
    ["review", "cuts"].includes(s.offseason?.stage);
}

/**
 * The portal day today (1-7) while coaches may pitch, else 0.
 * @param {object} league
 * @return {number}
 */
export function portalDay(league) {
  const s = league?.season;
  if (league?.type === "pro" || s?.phase !== "offseason") return 0;
  const stage = s.offseason?.stage;
  if (stage === "portal-open") return 1;
  const m = /^portal-(\d+)$/.exec(stage ?? "");
  return m ? Number(m[1]) : 0;
}

/**
 * Whether the portal list is worth showing: from the cut window until the
 * new season starts.
 * @param {object} league
 * @return {boolean}
 */
export function portalVisible(league) {
  const s = league?.season;
  return league?.type !== "pro" && s?.phase === "offseason" &&
    PORTAL_STAGES.concat(["review", "development"])
        .includes(s.offseason?.stage);
}
